import React, { useState, useEffect } from 'react';
import { Paper } from '@material-ui/core';
import { MenuItem, MenuList, ClickAwayListener, Grow, Popper, Divider, ListItemIcon, ListItemText } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import AddBoxOutlinedIcon from '@material-ui/icons/AddBoxOutlined';
import SettingsOverscanIcon from '@material-ui/icons/SettingsOverscan';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import ViewStreamIcon from '@material-ui/icons/ViewStream';
import LinkOutlinedIcon from '@material-ui/icons/LinkOutlined';
import ArrowRightAltOutlinedIcon from '@material-ui/icons/ArrowRightAltOutlined';
import FlipToFrontIcon from '@material-ui/icons/FlipToFront';
import FlipToBackIcon from '@material-ui/icons/FlipToBack';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import PriorityHighIcon from '@material-ui/icons/PriorityHigh';

import { SheetKinds } from '../redux/sheetDbSlice';
import { MapCard, CardDb, CardDbHashType, isStoryCard, isMapCard } from '../redux/cardDbSlice';
import { ShowMenuType, CardMenuAction } from './BookEditor';

type CardMenuProps = {
  showMenu: ShowMenuType;
  onClose: () => void;
  ids: string[];
  cards: CardDb[];
  cardMenuClicked: CardMenuAction;
  toggleIsGroupAndSave: (id: string, card: MapCard, isGroupOld?: boolean) => void;
  sheetKind: SheetKinds;
}
const CardMenu: React.FC<CardMenuProps> = ({ showMenu, onClose, ids, cards, cardMenuClicked, toggleIsGroupAndSave, sheetKind }) => {
  const [jumpDisabled, setJumpDisabled] = useState(true);
  const [enable, setEnable] = useState(false);
  const [showFront, setShowFront] = useState<EventTarget & HTMLElement | undefined>();
  const [showBack, setShowBack] = useState<EventTarget & HTMLElement | undefined>();
  type HandlingKey = 'edit' | 'childAdd' | 'resize' | 'copy' | 'lineCopy' | 'link' | 'jump' | 'forward' | 'foreground' | 'backward' | 'backmost' | 'delete'

  const card = showMenu?.card;
  const storyLine: CardDbHashType = {};
  if (sheetKind === 'story' && isStoryCard(card)) {
    ids.forEach((id, ix) => {
      const target = cards[ix];
      if (!isStoryCard(target) || !showMenu) return;
      if (id === showMenu.id || id === card.parentId || target.parentId === showMenu.id || (target.parentId && target.parentId === card.parentId)) storyLine[id] = target;
    })
  }

  const closeMenu = (ev: React.MouseEvent<EventTarget>) => {
    if (showMenu?.anchorEl && showMenu.anchorEl.contains(ev.target as HTMLElement)) return;
    onClose();
    frontMenuClose();
    backMenuClose();
  }
  const handlingExe = (ev: React.MouseEvent<EventTarget>, key: HandlingKey) => {
    if (!showMenu) return;
    const copiedCard: CardDbHashType = {};
    copiedCard[showMenu.id] = showMenu.card;

    switch (key) {
      case 'edit': cardMenuClicked.edit(showMenu.id); break;
      case 'childAdd': cardMenuClicked.childAdd(showMenu.parentCardId || showMenu.id, showMenu.card); break;
      case 'resize': cardMenuClicked.resize(showMenu.id); break;
      case 'copy': cardMenuClicked.copy(copiedCard); break;
      case 'lineCopy': cardMenuClicked.copy(storyLine); break;
      case 'link': cardMenuClicked.link(showMenu.id); break;
      case 'jump': cardMenuClicked.jump(showMenu.id); break;
      case 'forward': cardMenuClicked.forward(showMenu.id); break;
      case 'foreground': cardMenuClicked.foreground(showMenu.id); break;
      case 'backward': cardMenuClicked.backward(showMenu.id); break;
      case 'backmost': cardMenuClicked.backmost(showMenu.id); break;
      case 'delete': cardMenuClicked.delete(showMenu.id); break;
    }
    closeMenu(ev);
  }
  const toggleEnable = () => {
    setEnable(false);
    if (showMenu !== undefined) setTimeout(() => setEnable(true), 280)
  }
  const frontMenuOpen = (ev: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    backMenuClose();
    setShowFront(ev.currentTarget);
  }
  const frontMenuClose = () => {
    setShowFront(undefined);
  }
  const backMenuOpen = (ev: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    frontMenuClose();
    setShowBack(ev.currentTarget);
  }
  const backMenuClose = () => {
    setShowBack(undefined);
  }
  const subMenuClose = () => {
    frontMenuClose();
    backMenuClose();
  }
  const toggleIsGroup = (ev: React.MouseEvent<EventTarget>) => {
    const id = showMenu?.id
    if (!id || card?.kind !== 'common' || !isMapCard(card)) return;
    const newCard = { ...card, isGroup: !card.isGroup };
    toggleIsGroupAndSave(id, newCard, card.isGroup);
    closeMenu(ev);
  }

  useEffect(() => {
    toggleEnable();
    frontMenuClose();
    backMenuClose();
    if (showMenu?.card?.links && showMenu?.card?.links.length > 0) setJumpDisabled(false);
    else setJumpDisabled(true);
  }, [showMenu])

  return (
    <Popper open={showMenu !== undefined} anchorEl={showMenu?.anchorEl} role={undefined} transition disablePortal
      style={{zIndex: 998, pointerEvents: enable ? 'auto' : 'none'}}
    >
      { ({ TransitionProps, placement }) => (
        <Grow
          { ...TransitionProps }
          style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
        >
          <Paper>
            <ClickAwayListener onClickAway={closeMenu}>
              <MenuList autoFocusItem={showMenu !== undefined} id={'menu-list-grow'}>
                <MenuItem onClick={(ev) => handlingExe(ev, 'edit')} onMouseOver={subMenuClose}>
                  <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>カードの内容を編集する</ListItemText>
                </MenuItem>
                { sheetKind === 'story' &&
                  <MenuItem onClick={(ev) => handlingExe(ev, 'childAdd')} onMouseOver={subMenuClose}>
                    <ListItemIcon><AddBoxOutlinedIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>このカードの右側にカードを追加する</ListItemText>
                  </MenuItem>
                }
                { (sheetKind === 'map' && card?.kind !== 'ui') &&
                  <MenuItem onClick={(ev) => handlingExe(ev, 'resize')} onMouseOver={subMenuClose}>
                    <ListItemIcon><SettingsOverscanIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>このカードをリサイズする</ListItemText>
                  </MenuItem>
                }
                <MenuItem onClick={(ev) => handlingExe(ev, 'copy')} onMouseOver={subMenuClose}>
                  <ListItemIcon><FileCopyOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>このカードをコピーする</ListItemText>
                </MenuItem>
                { sheetKind === 'story' &&
                  <MenuItem onClick={(ev) => handlingExe(ev, 'lineCopy')} onMouseOver={subMenuClose}>
                    <ListItemIcon><ViewStreamIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>この行をコピーする</ListItemText>
                  </MenuItem>
                }
                <Divider />
                <MenuItem onClick={(ev) => handlingExe(ev, 'link')} onMouseOver={subMenuClose}>
                  <ListItemIcon><LinkOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>リンクを設定する</ListItemText>
                </MenuItem>
                <MenuItem onClick={(ev) => handlingExe(ev, 'jump')} onMouseOver={subMenuClose} disabled={jumpDisabled}>
                  <ListItemIcon><ArrowRightAltOutlinedIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>リンク先にジャンプする</ListItemText>
                </MenuItem>
                <Divider />
                { sheetKind === 'map' &&
                  <div>
                    <MenuItem onClick={(ev) => handlingExe(ev, 'forward')}
                      onMouseOver={ev => frontMenuOpen(ev)}
                      aria-controls="menu-list-grow-front"
                      aria-haspopup="true"
                    >
                      <ListItemIcon><FlipToFrontIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>前面に移動する</ListItemText>
                      <ListItemIcon><ArrowForwardIosIcon fontSize="small" /></ListItemIcon>
                    </MenuItem>
                    <Popper open={Boolean(showFront)} anchorEl={showFront} role={undefined} transition disablePortal
                      style={{zIndex: 999}} placement="right"
                    >
                      { ({ TransitionProps, placement }) => (
                        <Grow
                          { ...TransitionProps }
                          style={{ transformOrigin: placement === 'right' ? 'left center' : 'right center' }}
                        >
                          <Paper>
                            <ClickAwayListener onClickAway={frontMenuClose}>
                              <MenuList autoFocusItem={Boolean(showFront)} id="menu-list-grow-front">
                                <MenuItem onClick={(ev) => handlingExe(ev, 'foreground')}>
                                  <ListItemIcon><FlipToFrontIcon fontSize="small" /></ListItemIcon>
                                  <ListItemText>最前面に移動する</ListItemText>
                                </MenuItem>
                                <MenuItem onClick={(ev) => handlingExe(ev, 'forward')}>
                                  <ListItemIcon><FlipToFrontIcon fontSize="small" /></ListItemIcon>
                                  <ListItemText>前面に移動する</ListItemText>
                                </MenuItem>
                              </MenuList>
                            </ClickAwayListener>
                          </Paper>
                        </Grow>
                      ) }
                    </Popper>
                    <MenuItem onClick={(ev) => handlingExe(ev, 'backward')}
                      onMouseOver={ev => backMenuOpen(ev)}
                      aria-controls="menu-list-grow-back"
                      aria-haspopup="true"
                    >
                      <ListItemIcon><FlipToBackIcon fontSize="small" /></ListItemIcon>
                      <ListItemText>背面に移動する</ListItemText>
                      <ListItemIcon><ArrowForwardIosIcon fontSize="small" /></ListItemIcon>
                    </MenuItem>
                    <Popper open={Boolean(showBack)} anchorEl={showBack} role={undefined} transition disablePortal
                      style={{zIndex: 999}} placement="right"
                    >
                      { ({ TransitionProps, placement }) => (
                        <Grow
                          { ...TransitionProps }
                          style={{ transformOrigin: placement === 'right' ? 'left center' : 'right center' }}
                        >
                          <Paper>
                            <ClickAwayListener onClickAway={backMenuClose}>
                              <MenuList autoFocusItem={Boolean(showBack)} id="menu-list-grow-back">
                                <MenuItem onClick={(ev) => handlingExe(ev, 'backmost')}>
                                  <ListItemIcon><FlipToBackIcon fontSize="small" /></ListItemIcon>
                                  <ListItemText>最背面に移動する</ListItemText>
                                </MenuItem>
                                <MenuItem onClick={(ev) => handlingExe(ev, 'backward')}>
                                  <ListItemIcon><FlipToBackIcon fontSize="small" /></ListItemIcon>
                                  <ListItemText>背面に移動する</ListItemText>
                                </MenuItem>
                              </MenuList>
                            </ClickAwayListener>
                          </Paper>
                        </Grow>
                      ) }
                    </Popper>
                    <Divider />
                    { card?.kind === 'common' &&
                      <>
                        <MenuItem onClick={toggleIsGroup} onMouseOver={subMenuClose}>
                          <ListItemIcon><PriorityHighIcon fontSize="small" /></ListItemIcon>
                          <ListItemText>{ card.isGroup ? 'グルーピングを解除する' : 'グルーピングカードにする' }</ListItemText>
                        </MenuItem>
                        <Divider />
                      </>
                    }
                  </div>
                }
                <MenuItem onClick={(ev) => handlingExe(ev, 'delete')} onMouseOver={subMenuClose}>
                  <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
                  <ListItemText>このカードを削除する</ListItemText>
                </MenuItem>
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      ) }
    </Popper>
  );
}

export default CardMenu;
